/* eslint-disable max-lines */
/* istanbul ignore file */
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { getPeriodDisplayValue } from './services/projects.services';
import { iterationTitle } from './services/projectExtended.services';
import babyMobile from 'images/baby-mobile.svg';
import hourGlass from 'images/icons/hourglass.svg';
import { LICENSE_PLAN_NAMES } from '@linearb/linjs-common';
import { calculatePercentage } from 'common/utils/utils';
import moment from 'moment';
import {
  MONTHS_PARAM,
  SEARCH_DATE_FORMAT,
  YEARS_PARAM
} from 'common/constants/constants';
import { getPmViewerFromStore } from 'components/hooks/PM/pm-provider-viewers';
import { capitalize } from 'lodash';

export const LICENSE_SHOW_EMPTY_STATE = [
  LICENSE_PLAN_NAMES.FREE,
  LICENSE_PLAN_NAMES.PRO
];

export const SHOW_DECIMAL_POINT_BELOW = 10;

export const getIssueTypesOptions = () => {
  const pmViewer = getPmViewerFromStore();
  return [
    {
      key: 'counts',
      displayValue: `${capitalize(pmViewer.ticket.single)} count`
    },
    {
      key: 'story_points',
      displayValue: `${capitalize(pmViewer.storyPoint.plural)}`
    }
  ];
};

export const PROJECTS_VIEW_TYPES = {
  EXTENDED_CHART_VIEW: 'extendedChartView',
  EXTENDED_PIE_VIEW: 'extendedPieView',
  ITERATION_VIEW: 'iterationView',
  SUMMARY_VIEW: 'summaryView',
  PROJECTS_VIEW: 'projectsView'
};

export const SUMMARY_PERIODS = {
  single_iteration: 'I',
  last_sprint: 'L',
  last_3_months: '3',
  last_6_months: '6',
  quarter_to_date: 'Q',
  year_to_date: 'Y',
  last_30_days: '1',
  custom: 'C'
};

export const PROJECTS_PERIODS_OPTIONS = (lastIteration, singleIteration) => [
  ...(singleIteration?.id === lastIteration?.id
    ? []
    : [
        {
          value: SUMMARY_PERIODS.single_iteration,
          displayValue: iterationTitle(singleIteration)
        }
      ]),
  {
    value: SUMMARY_PERIODS.last_sprint,
    displayValue: `Last iteration${
      lastIteration ? ` - ${iterationTitle(lastIteration)}` : ''
    }`
  },
  { value: SUMMARY_PERIODS.last_30_days, displayValue: 'Last 30 days' },
  { value: SUMMARY_PERIODS.last_3_months, displayValue: 'Last 3 months' },
  { value: SUMMARY_PERIODS.last_6_months, displayValue: 'Last 6 months' },
  { value: SUMMARY_PERIODS.quarter_to_date, displayValue: 'Quarter to date' },
  { value: SUMMARY_PERIODS.year_to_date, displayValue: 'Year to date' },
  { value: SUMMARY_PERIODS.custom, displayValue: 'Custom range' }
];

export const PROJECTS_PERIODS_TEXT = {
  [SUMMARY_PERIODS.last_30_days]: {
    avg_title: '30 days average for iteration:'
  },
  [SUMMARY_PERIODS.last_3_months]: {
    avg_title: '3 month average for iteration:'
  },
  [SUMMARY_PERIODS.last_6_months]: {
    avg_title: '6 month average for iteration:'
  },
  [SUMMARY_PERIODS.quarter_to_date]: {
    avg_title: 'Last quarter average for iteration:'
  },
  [SUMMARY_PERIODS.year_to_date]: {
    avg_title: 'Last year average for iteration:'
  },
  [SUMMARY_PERIODS.custom]: {
    avg_title: 'Average for iteration:'
  }
};

export const SUMMARY_PERIODS_DEFAULT = SUMMARY_PERIODS.last_3_months;

export const columns = [
  {
    name: 'PROJECTS',
    dataPoint: 'projects',
    subscribeOnHover: true,
    tableCellProps: {
      style: { borderRight: 0, borderLeft: 0, width: '25%' }
    }
  },
  {
    name: 'ACTIVE PEOPLE',
    dataPoint: 'people_effort',
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    name: '',
    dataPoint: 'people_effort_over_time',
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    name: 'INVESTMENT BREAKDOWN',
    dataPoint: 'issues_breakdown',
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    name: 'DELIVERY',
    dataPoint: 'issues_over_time',
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    name: '',
    dataPoint: 'menu_button',
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  }
];

export const PROJECT_EMPTY_STATES_KEYS = {
  noProjectData: 'noProjectData',
  noPeopleEffort: 'noPeopleEffort',
  noIssueTypesData: 'noIssueTypesData',
  noDeliveryData: 'noDeliveryData',
  newProjectLoading: 'newProjectLoading'
};

export const PROJECT_EMPTY_STATES = {
  [PROJECT_EMPTY_STATES_KEYS.newProjectLoading]: {
    image: hourGlass,
    imageSize: '2rem',
    messageComponent: (
      <Flex direction='column' ml='medium'>
        <Text weight='500' size='medium' mb='0.3rem'>
          This project is on its way...
        </Text>
        <Text color='shuttleGray'>
          We’re analyzing and organizing the data.
        </Text>
      </Flex>
    )
  },
  [PROJECT_EMPTY_STATES_KEYS.noProjectData]: selectedPeriod => ({
    image: babyMobile,
    imageSize: '3.5rem',
    message: `No Activity in the ${getPeriodDisplayValue(selectedPeriod)}`
  }),
  [PROJECT_EMPTY_STATES_KEYS.noPeopleEffort]: {
    icon: 'teamspeopleicon',
    message: `No active people`
  },
  [PROJECT_EMPTY_STATES_KEYS.noIssueTypesData]: pmViewer => ({
    icon: 'coffeecup',
    message: `No activity on ${pmViewer.ticket.plural}`
  }),
  [PROJECT_EMPTY_STATES_KEYS.noDeliveryData]: {
    icon: 'coffeecup',
    message: `No activity on delivery`
  }
};

export const DELIVERY_STATES_API_KEYS = {
  PLANNED: 'planned',
  COMPLETED: 'planned_completed',
  ADDED: 'added_completed',
  UNCOMPLETED: 'planned_uncompleted',
  ADDED_UNCOMPLETED: 'added_uncompleted',
  DONE: 'done'
};

export const DELIVERY_STATES_KEYS = {
  [DELIVERY_STATES_API_KEYS.PLANNED]: {
    title: 'Planned',
    projectsViewTitle: 'Planned',
    issuesTableTitle: 'Planned',
    titleTooltip: 'Planned',
    projectsViewTitleTooltip: 'Planned',
    color: 'shark'
  },
  [DELIVERY_STATES_API_KEYS.COMPLETED]: {
    title: 'Completed',
    projectsViewTitle: 'Completed',
    issuesTableTitle: 'Planned & Completed',
    titleTooltip: 'Completed',
    projectsViewTitleTooltip: 'Planned and completed',
    color: 'funGreen'
  },
  [DELIVERY_STATES_API_KEYS.ADDED]: {
    title: 'Completed',
    projectsViewTitle: 'Unplanned',
    issuesTableTitle: 'Added work & Completed',
    titleTooltip: 'Completed',
    projectsViewTitleTooltip: 'Unplanned and completed',
    color: 'madang'
  },
  [DELIVERY_STATES_API_KEYS.UNCOMPLETED]: {
    title: 'Uncompleted',
    projectsViewTitle: 'Uncompleted',
    issuesTableTitle: 'Planned & Uncompleted',
    titleTooltip: 'Uncompleted',
    projectsViewTitleTooltip: 'Uncompleted',
    color: 'peachOrange'
  },
  [DELIVERY_STATES_API_KEYS.DONE]: {
    color: 'pastelGreen'
  },
  [DELIVERY_STATES_API_KEYS.ADDED_UNCOMPLETED]: {
    title: 'Uncompleted',
    projectsViewTitle: 'Uncompleted',
    issuesTableTitle: 'Added work & Uncompleted',
    titleTooltip: 'Uncompleted',
    projectsViewTitleTooltip: 'Uncompleted',
    color: 'cerulean'
  }
};

export const SINGLE_ITERATIONS_PERIODS = [
  SUMMARY_PERIODS.last_sprint,
  SUMMARY_PERIODS.single_iteration
];

export const SCRUM_SORTED_ISSUES_KEYS = [
  DELIVERY_STATES_API_KEYS.COMPLETED,
  DELIVERY_STATES_API_KEYS.ADDED,
  DELIVERY_STATES_API_KEYS.UNCOMPLETED,
  DELIVERY_STATES_API_KEYS.PLANNED
];

// Used for stacked columns chart. Planned needs to be last so its line marker will appear before bar
export const SCRUM_SORTED_CHART_KEYS = [
  DELIVERY_STATES_API_KEYS.ADDED,
  DELIVERY_STATES_API_KEYS.COMPLETED,
  DELIVERY_STATES_API_KEYS.PLANNED
];

const BAR_TYPE = {
  PROGRESS: 'progress',
  RANGES: 'ranges'
};

export const DELIVERY_BREAKDOWN = {
  [DELIVERY_STATES_API_KEYS.PLANNED]: {
    title: 'Total Planned',
    breakdown: [
      {
        key: DELIVERY_STATES_API_KEYS.COMPLETED,
        title: 'Completed',
        color: DELIVERY_STATES_KEYS[DELIVERY_STATES_API_KEYS.COMPLETED].color
      },
      {
        key: DELIVERY_STATES_API_KEYS.UNCOMPLETED,
        title: 'Uncompleted',
        color: DELIVERY_STATES_KEYS[DELIVERY_STATES_API_KEYS.ADDED].color
      }
    ]
  },
  [DELIVERY_STATES_API_KEYS.ADDED]: {
    title: 'Total Added Work',
    breakdown: [
      {
        key: DELIVERY_STATES_API_KEYS.ADDED,
        title: 'Completed',
        color: 'waikawaGray'
      },
      {
        key: DELIVERY_STATES_API_KEYS.ADDED_UNCOMPLETED,
        title: 'Uncompleted',
        color:
          DELIVERY_STATES_KEYS[DELIVERY_STATES_API_KEYS.ADDED_UNCOMPLETED].color
      }
    ]
  },
  [DELIVERY_STATES_API_KEYS.COMPLETED]: {
    title: 'Completed',
    breakdown: [
      {
        key: DELIVERY_STATES_API_KEYS.COMPLETED,
        title: 'Planned',
        color: DELIVERY_STATES_KEYS[DELIVERY_STATES_API_KEYS.COMPLETED].color
      },
      {
        key: DELIVERY_STATES_API_KEYS.ADDED,
        title: 'Unplanned',
        color: DELIVERY_STATES_KEYS[DELIVERY_STATES_API_KEYS.ADDED].color
      }
    ]
  },
  [DELIVERY_STATES_API_KEYS.UNCOMPLETED]: {
    title: 'Not completed',
    breakdown: [
      {
        key: DELIVERY_STATES_API_KEYS.UNCOMPLETED,
        title: 'Planned',
        color: 'orangePeel'
      },
      {
        key: DELIVERY_STATES_API_KEYS.ADDED_UNCOMPLETED,
        title: 'Unplanned',
        color:
          DELIVERY_STATES_KEYS[DELIVERY_STATES_API_KEYS.ADDED_UNCOMPLETED].color
      }
    ]
  }
};

const ACCURACY_TYPES = {
  PLANNING: 'planning',
  CAPACITY: 'capacity'
};

export const CAPACITY_RANGES = [
  {
    range: { min: 131, max: 300 },
    title: 'Getting There!',
    description: <>Consider increasing the capacity of work</>,
    text: ({ completed, added, planned }) => {
      return (
        <>
          Based on the calculation of <b>total completed work</b> (
          {completed + added}), divided by <b>planned work</b> ({planned}),
          <br></br>
          It looks like your team is not taking on enough work.<br></br>
          Improve team capacity by increasing the amount of planned work.
        </>
      );
    },
    contentColor: 'radicalRed',
    statusIconName: 'StatusWarning'
  },
  {
    range: { min: 116, max: 130 },
    title: 'Great Capacity Accuracy!',
    description: (
      <>
        Consider taking it to the next level by <br />
        increasing the capacity work
      </>
    ),
    text: ({ completed, added, planned }) => {
      return (
        <>
          Based on the calculation of <b>total completed work</b> (
          {completed + added}), divided by <b>planned work</b> ({planned}),
          <br></br>
          Your team might not be taking on enough work. Improve team capacity by
          increasing<br></br>
          the amount of planned work.
        </>
      );
    },
    contentColor: 'orangePeel',
    statusIconName: 'StatusFair'
  },
  {
    range: { min: 85, max: 115 },
    title: 'Great Capacity Accuracy!',
    text: ({ completed, added, planned }) => {
      return (
        <>
          Based on the calculation of <b>total completed work</b> (
          {completed + added}), divided by <b>planned work</b> ({planned}),
          <br></br>
          It looks like your team is taking on the correct amount of work. Good
          job!
        </>
      );
    },
    contentColor: 'funGreen',
    statusIconName: 'StatusGood'
  },
  {
    range: { min: 70, max: 84 },
    title: 'Getting There!',
    description: <>Consider decreasing the capacity of work</>,
    text: ({ completed, added, planned }) => {
      return (
        <>
          Based on the calculation of <b>total completed work</b> (
          {completed + added}), divided by <b>planned work</b> ({planned}),
          <br></br>
          Our team might be taking on too much work. Try reducing the amount of
          planned<br></br>
          work to increase your delivery accuracy.
        </>
      );
    },
    contentColor: 'orangePeel',
    statusIconName: 'StatusFair'
  },
  {
    range: { min: 0, max: 69 },
    title: 'Decrease the Capacity of Work',
    text: ({ completed, added, planned }) => {
      return (
        <>
          Based on the calculation of <b>total completed work</b> (
          {completed + added}), divided by <b>planned work</b> ({planned}),
          <br></br>
          It looks like your team is commiting to too much work. Try reducing
          the amount of<br></br>
          planned work to increase your delivery accuracy.
        </>
      );
    },
    contentColor: 'radicalRed',
    statusIconName: 'StatusWarning'
  }
];

const PLANNING_RANGES = [
  {
    range: { min: 96, max: 100 },
    title: 'Potential Under Commit',
    description: (
      <>
        Consider taking it to the next level by <br />
        increasing the capacity of planned work
      </>
    ),
    text: ({ completed, planned }) => {
      const pmViewer = getPmViewerFromStore();
      return (
        <>
          Planning accuracy measures the ratio between{' '}
          <b>planned {pmViewer.ticket.plural}</b> ({planned}) and what<br></br>
          was <b>actually delivered</b> from that list ({completed}). Your team
          might be under-committing,<br></br>
          consider taking on more planned work.
        </>
      );
    },
    contentColor: 'orangePeel',
    statusIconName: 'StatusFair'
  },
  {
    range: { min: 90, max: 95 },
    title: 'Great Planning Accuracy!',
    text: ({ completed, planned }) => {
      const pmViewer = getPmViewerFromStore();
      return (
        <>
          Planning accuracy measures the ratio between{' '}
          <b>planned {pmViewer.ticket.plural}</b> ({planned}) and what<br></br>
          was <b>actually delivered</b> from that list ({completed}). Great
          work!
        </>
      );
    },
    contentColor: 'funGreen',
    statusIconName: 'StatusGood'
  },
  {
    range: { min: 70, max: 89 },
    title: 'Getting There!',
    description: <> Prioritize commitment to planned work</>,
    text: ({ completed, planned }) => {
      const pmViewer = getPmViewerFromStore();
      return (
        <>
          Planning accuracy measures the ratio between{' '}
          <b>planned {pmViewer.ticket.plural}</b> ({planned})<br></br>
          and what was <b>actually delivered</b> from that list ({completed}).
          You're getting there!
        </>
      );
    },
    contentColor: 'orangePeel',
    statusIconName: 'StatusFair'
  },
  {
    range: { min: 0, max: 69 },
    title: 'Prioritize Planned Work',
    text: ({ completed, planned }) => {
      const pmViewer = getPmViewerFromStore();
      return (
        <>
          Planning accuracy measures the ratio between{' '}
          <b>planned {pmViewer.ticket.plural}</b> ({planned}) and what <br></br>
          was <b>actually delivered</b> from that list ({completed}). Aim for
          70% to start improving<br></br>
          planning accuracy.
        </>
      );
    },
    contentColor: 'radicalRed',
    statusIconName: 'StatusWarning'
  }
];

export const ACCURACY_BUILDER = {
  [ACCURACY_TYPES.PLANNING]: {
    key: ACCURACY_TYPES.PLANNING,
    ranges: PLANNING_RANGES,
    icon: 'Accuracy',
    graphIcon: 'PlanningAccuracyGraphLegend',
    title: 'Planning accuracy',
    color: 'melrose',
    dashStyle: 'solid',
    barType: BAR_TYPE.PROGRESS,
    subtitle: (
      <>
        Planning accuracy answers{' '}
        <b>
          "How well is my team executing and <br></br>
          prioritizing their planned work"?
        </b>
      </>
    ),
    totalsCalculation: ({
      [DELIVERY_STATES_API_KEYS.PLANNED]: planned,
      [DELIVERY_STATES_API_KEYS.COMPLETED]: completed
    }) => {
      return calculatePercentage(completed, planned);
    }
  },
  [ACCURACY_TYPES.CAPACITY]: {
    key: ACCURACY_TYPES.CAPACITY,
    ranges: CAPACITY_RANGES,
    icon: 'Accuracy',
    graphIcon: 'PlanningAccuracyGraphLegendDash',
    title: 'Capacity accuracy',
    color: 'indigo',
    dashStyle: 'dash',
    barType: BAR_TYPE.RANGES,
    subtitle: (
      <>
        Capacity accuracy answers{' '}
        <b>
          "Is my team taking on an amount of<br></br> work that they can
          reasonably accomplish"?
        </b>
      </>
    ),
    totalsCalculation: ({
      [DELIVERY_STATES_API_KEYS.PLANNED]: planned,
      [DELIVERY_STATES_API_KEYS.COMPLETED]: completed,
      [DELIVERY_STATES_API_KEYS.ADDED]: added
    }) => {
      const percentage =
        planned > 0 ? ((completed + added) / planned) * 100 : 0;
      return Math.round(percentage);
    }
  }
};

export const CUSTOM_DEFAULT_START_DATE = moment()
  .subtract(1, MONTHS_PARAM)
  .format(SEARCH_DATE_FORMAT);
export const CUSTOM_DEFAULT_END_DATE = moment().format(SEARCH_DATE_FORMAT);

export const CUSTOM_RANGE_MIN_DATE = moment().subtract(1, YEARS_PARAM);

export const MAX_ISSUES_KEYS = 5;

export const PDT_PAGE_SIZE = 10;

export const PDT_PAGE_SIZE_OPTIONS = [
  { value: 5, displayValue: 5 },
  { value: 10, displayValue: 10 },
  { value: 25, displayValue: 25 }
];

export const PDT_EMPTY_STATE_TYPES = {
  NO_DATA: 'no-data',
  NO_SEARCH_RESULTS: 'no-search-results'
};
